import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, clearAllNotifications } from '../../../api/customerDashboardApi.js';
import { classifyPortalError } from '../utils/apiError.js';
import { showErrorToast } from '../utils/toast.js';
import Modal from './Modal.jsx';
import BubbleLoader from './BubbleLoader.jsx';
import './NotificationModal.css';

const LOAD_ERROR_FALLBACK = "Couldn't load notifications. Please try again.";
const CLEAR_ERROR_FALLBACK = "Couldn't clear notifications. Please try again.";

function notifyError(err, fallback) {
  showErrorToast(classifyPortalError(err).message || fallback);
}

function formatTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHrs < 24) return `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function NotificationModal({ open, onClose }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const sentinelRef = useRef(null);
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;

  useEffect(() => {
    if (!open) return;
    setItems([]);
    setCursor(null);
    setHasMore(false);
    setInitialLoading(true);
    getNotifications(20)
      .then((res) => {
        setItems(res.data);
        setCursor(res.cursor);
        setHasMore(res.hasMore);
      })
      .catch((err) => notifyError(err, LOAD_ERROR_FALLBACK))
      .finally(() => setInitialLoading(false));
  }, [open]);

  useEffect(() => {
    if (!hasMore || loading || !sentinelRef.current) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setLoading(true);
        getNotifications(20, cursorRef.current)
          .then((res) => {
            setItems((prev) => [...prev, ...res.data]);
            setCursor(res.cursor);
            setHasMore(res.hasMore);
          })
          .catch((err) => notifyError(err, LOAD_ERROR_FALLBACK))
          .finally(() => setLoading(false));
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  async function handleClearAll() {
    setClearing(true);
    try {
      await clearAllNotifications();
      setItems([]);
      setCursor(null);
      setHasMore(false);
    } catch (err) {
      notifyError(err, CLEAR_ERROR_FALLBACK);
    } finally {
      setClearing(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Notifications"
      onClose={onClose}
      className="customer-portal-notif-modal"
      headerActions={
        items.length > 0 && (
          <button className="customer-portal-notif-modal__clear-all" onClick={handleClearAll} disabled={clearing}>
            {clearing ? 'Clearing…' : 'Clear all'}
          </button>
        )
      }
    >
      <div className="customer-portal-notif-modal__list">
        {initialLoading && (
          <div className="customer-portal-notif-modal__loader"><BubbleLoader /></div>
        )}

        {!initialLoading && items.length === 0 && (
          <div className="customer-portal-notif-modal__empty">No notifications yet</div>
        )}

        {items.map((n, i) => {
          const isClickable = n.url?.startsWith('/customer');
          const content = (
            <>
              <div className="customer-portal-notif-modal__item-title">{n.title}</div>
              <div className="customer-portal-notif-modal__item-message">{n.message}</div>
              <div className="customer-portal-notif-modal__item-time">{formatTime(n.createdAt)}</div>
            </>
          );
          return isClickable ? (
            <button
              key={n.cursor ?? i}
              type="button"
              className="customer-portal-notif-modal__item customer-portal-notif-modal__item--clickable"
              onClick={() => { navigate(n.url); onClose(); }}
            >
              {content}
            </button>
          ) : (
            <div key={n.cursor ?? i} className="customer-portal-notif-modal__item">
              {content}
            </div>
          );
        })}

        {hasMore && <div ref={sentinelRef} className="customer-portal-notif-modal__sentinel" />}
        {loading && <div className="customer-portal-notif-modal__loader"><BubbleLoader /></div>}
        {!hasMore && items.length > 0 && (
          <div className="customer-portal-notif-modal__end">That&apos;s all</div>
        )}
      </div>
    </Modal>
  );
}
